import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Flag, Gavel, MessageSquare, ShieldCheck, Trash2, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BanUserDialog from "@/components/BanUserDialog";

const REASON_LABELS = {
  // Server reports
  spam: "Spam / self-promo",
  scam: "Scam / phishing",
  inappropriate: "NSFW / hateful",
  misleading: "Misleading",
  underage: "Minors involved",
  // Review reports
  harassment: "Harassment",
  "off-topic": "Off-topic",
  other: "Other",
};

const STATUSES = [
  { id: "open", label: "Open" },
  { id: "resolved", label: "Resolved" },
  { id: "dismissed", label: "Dismissed" },
  { id: "all", label: "All" },
];

function relTime(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function AdminReports() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [totalOpen, setTotalOpen] = useState(0);
  const [filter, setFilter] = useState("open");
  const [busy, setBusy] = useState(null);
  const [banTarget, setBanTarget] = useState(null); // { user_id, name }

  useEffect(() => {
    if (loading) return;
    if (!user || (user.role !== "admin" && user.role !== "owner")) navigate("/");
  }, [user, loading, navigate]);

  const load = useCallback(async () => {
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/admin/reports", { params });
      setItems(data.items || []);
      setTotalOpen(data.total_open || 0);
    } catch (err) {
      toast.error("Failed to load reports");
    }
  }, [filter]);

  useEffect(() => {
    if (!user || (user.role !== "admin" && user.role !== "owner")) return;
    load();
    const interval = setInterval(load, 20000);
    return () => clearInterval(interval);
  }, [user, load]);

  const act = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/admin/reports/${id}/${action}`);
      toast.success(action === "resolve" ? "Marked resolved" : "Dismissed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(null);
    }
  };

  const deleteServer = async (serverId, serverName) => {
    if (!confirm(`Delete server "${serverName}"? This can't be undone.`)) return;
    setBusy(serverId);
    try {
      await api.delete(`/servers/${serverId}`);
      toast.success("Server deleted");
      // Auto-resolve any open reports for this server
      const openReports = items.filter(
        (r) => r.server_id === serverId && r.status === "open"
      );
      await Promise.allSettled(
        openReports.map((r) =>
          api.post(`/admin/reports/${r.id}/resolve`).catch(() => {})
        )
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(null);
    }
  };

  const deleteReview = async (report) => {
    if (!confirm(`Delete this review by ${report.reviewer_name}? This can't be undone.`)) return;
    setBusy(report.id);
    try {
      await api.delete(`/servers/${report.server_id}/reviews/${report.review_id}`);
      toast.success("Review deleted");
      // Auto-resolve every open report for the same review
      const openReports = items.filter(
        (r) => r.target === "review" && r.review_id === report.review_id && r.status === "open"
      );
      await Promise.allSettled(
        openReports.map((r) =>
          api.post(`/admin/reports/${r.id}/resolve`).catch(() => {})
        )
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(null);
    }
  };

  if (loading || !user) return null;

  return (
    <div className="min-h-screen bg-[#05050A] text-white pt-24 pb-16 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => navigate("/admin")}
          data-testid="back-to-admin"
          className="mb-4 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" /> Back to admin
        </button>

        <div className="flex items-center gap-3 mb-1">
          <Flag className="w-6 h-6 text-red-400" />
          <h1 className="font-heading text-3xl font-black">Reports</h1>
          {totalOpen > 0 && (
            <span
              className="ml-2 min-w-[24px] h-6 px-2 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center"
              data-testid="reports-open-count"
            >
              {totalOpen}
            </span>
          )}
        </div>
        <p className="text-sm text-zinc-500">
          User-submitted reports about listings. Resolve genuine issues, dismiss noise.
        </p>

        <div className="mt-6 flex items-center gap-2 flex-wrap">
          {STATUSES.map((s) => (
            <button
              key={s.id}
              onClick={() => setFilter(s.id)}
              data-testid={`filter-${s.id}`}
              className={`h-9 px-3 rounded-lg text-sm font-semibold transition-colors ${
                filter === s.id
                  ? "bg-white/10 border border-white/20 text-white"
                  : "bg-transparent border border-white/5 text-zinc-500 hover:border-white/10 hover:text-white"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-3">
          {items.length === 0 ? (
            <div className="text-center py-20 rounded-2xl border border-dashed border-white/10 text-sm text-zinc-500" data-testid="reports-empty">
              No {filter === "all" ? "" : filter} reports.
            </div>
          ) : (
            items.map((r) => (
              <div
                key={r.id}
                data-testid={`report-${r.id}`}
                className="rounded-2xl border border-white/5 bg-[#0A0A0F] p-5"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-red-500/15 text-red-300 border border-red-500/30">
                        {REASON_LABELS[r.reason] || r.reason}
                      </span>
                      {r.target === "review" && (
                        <span
                          data-testid={`target-review-${r.id}`}
                          className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-blue-500/15 text-blue-300 border border-blue-500/40 inline-flex items-center gap-1"
                        >
                          <MessageSquare className="w-3 h-3" /> Review
                        </span>
                      )}
                      {r.status !== "open" && (
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                            r.status === "resolved"
                              ? "bg-neon-green/15 text-neon-green border border-neon-green/40"
                              : "bg-zinc-700/60 text-zinc-400"
                          }`}
                        >
                          {r.status}
                        </span>
                      )}
                      <span className="text-[10px] font-mono text-zinc-500">
                        {relTime(r.created_at)}
                      </span>
                    </div>

                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <Link
                        to={`/servers/${r.server_slug || r.server_id}`}
                        target="_blank"
                        className="inline-flex items-center gap-1 font-semibold text-white hover:text-blurple"
                      >
                        {r.server_name || r.server_id}
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                      <span className="text-xs text-zinc-500">
                        reported by{" "}
                        <span className="text-zinc-300">{r.reporter_name}</span>
                      </span>
                      {r.by_banned_user && (
                        <span
                          data-testid={`banned-flag-${r.id}`}
                          className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-red-500/15 text-red-300 border border-red-500/40"
                        >
                          banned reporter
                        </span>
                      )}
                    </div>

                    {r.details && (
                      <div className="mt-2 rounded-lg bg-[#12121A] border border-white/5 px-3 py-2 text-sm text-zinc-300 whitespace-pre-wrap break-words">
                        {r.details}
                      </div>
                    )}

                    {r.target === "review" && (
                      <div
                        data-testid={`reported-review-${r.id}`}
                        className="mt-3 rounded-lg border border-blue-500/30 bg-blue-500/[0.04] px-3 py-2"
                      >
                        <div className="text-[10px] font-bold uppercase tracking-widest text-blue-300 mb-1 flex items-center gap-2">
                          <MessageSquare className="w-3 h-3" /> Reported review
                          {typeof r.review_rating === "number" && (
                            <span className="text-yellow-300">{"★".repeat(r.review_rating)}</span>
                          )}
                          <span className="text-zinc-500">
                            by <span className="text-zinc-300">{r.reviewer_name || r.reviewer_id}</span>
                          </span>
                        </div>
                        <p className="text-sm text-zinc-200 whitespace-pre-wrap break-words">
                          {r.review_comment || <em className="text-zinc-500">(no comment)</em>}
                        </p>
                      </div>
                    )}

                    {r.resolved_by_name && (
                      <div className="mt-2 text-[10px] font-mono text-zinc-500">
                        {r.status === "resolved" ? "resolved" : "dismissed"} by {r.resolved_by_name} · {relTime(r.resolved_at)}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {r.status === "open" && (
                      <>
                        <button
                          onClick={() => act(r.id, "resolve")}
                          disabled={busy === r.id}
                          data-testid={`resolve-${r.id}`}
                          className="h-9 px-3 rounded-lg bg-neon-green/10 hover:bg-neon-green/20 border border-neon-green/40 text-neon-green text-xs font-bold inline-flex items-center gap-1.5"
                        >
                          <ShieldCheck className="w-3.5 h-3.5" /> Resolve
                        </button>
                        <button
                          onClick={() => act(r.id, "dismiss")}
                          disabled={busy === r.id}
                          data-testid={`dismiss-${r.id}`}
                          className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300 text-xs font-bold inline-flex items-center gap-1.5"
                        >
                          <X className="w-3.5 h-3.5" /> Dismiss
                        </button>
                        {!r.by_banned_user && (
                          <button
                            onClick={() =>
                              setBanTarget({
                                user_id: r.reporter_id,
                                name: r.reporter_name,
                              })
                            }
                            data-testid={`ban-reporter-${r.id}`}
                            className="h-9 px-3 rounded-lg bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/40 text-purple-300 text-xs font-bold inline-flex items-center gap-1.5"
                            title="Ban this reporter across the site"
                          >
                            <Gavel className="w-3.5 h-3.5" /> Ban reporter
                          </button>
                        )}
                        {r.target === "review" ? (
                          <button
                            onClick={() => deleteReview(r)}
                            disabled={busy === r.id}
                            data-testid={`delete-review-${r.id}`}
                            className="h-9 px-3 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-bold inline-flex items-center gap-1.5"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete review
                          </button>
                        ) : (
                          <button
                            onClick={() => deleteServer(r.server_id, r.server_name)}
                            disabled={busy === r.server_id}
                            data-testid={`delete-server-${r.id}`}
                            className="h-9 px-3 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-bold"
                          >
                            Delete server
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <BanUserDialog
        open={!!banTarget}
        onOpenChange={(v) => !v && setBanTarget(null)}
        target={banTarget}
        defaultPurgeReviews={true}
        onBanned={load}
      />
    </div>
  );
}
